import {
    type CalendarDate,
    browserTimezone,
    todayIn,
    startOf,
    endOf,
    addDays,
    addWeeks,
    addMonths,
    diffDays,
    diffMonths,
} from '@/utils/dt';

export type ChartViewMode = 'days' | 'weeks' | 'months';

/**
 * Clamps a desired window to fit inside an allowed [minStart..maxEnd] range.
 * ISO date string comparison (lexicographic) works correctly for YYYY-MM-DD.
 */
const clampRange = (
    desiredStart: CalendarDate,
    desiredEnd: CalendarDate,
    minStart: CalendarDate,
    maxEnd: CalendarDate,
): { start: CalendarDate; end: CalendarDate } => {
    const start = desiredStart < minStart ? minStart : desiredStart;
    const end = desiredEnd > maxEnd ? maxEnd : desiredEnd;
    return start <= end ? { start, end } : { start: minStart, end: maxEnd };
};

/**
 * DAYS mode:
 * - If the provided range is <= maxBars days: keep it as-is.
 * - If range > maxBars days:
 *   - If the range contains today: choose a maxBars-day window placing today at index
 *     `floor(maxBars / 2)` — 6th of 10, 11th of 20 — **if the range reaches far enough
 *     into the future**. See {@link normalizePaymentsChartRange} on why that proviso decides
 *     everything: a range ending at today pins today to the last bar instead.
 *   - If not: choose the maxBars-day window closest to today (head or tail of the range).
 */
const getDaysRange = (from: CalendarDate, to: CalendarDate, today: CalendarDate, maxBars = 10): { from: CalendarDate; to: CalendarDate } => {
    const totalDays = diffDays(from, to) + 1;
    if (totalDays <= maxBars) return { from, to };

    const includesToday = today >= from && today <= to;
    if (!includesToday) {
        if (today < from) return { from, to: addDays(from, maxBars - 1) };
        return { from: addDays(to, -(maxBars - 1)), to };
    }

    const desiredStart = addDays(today, -Math.floor(maxBars / 2));
    const desiredEnd = addDays(desiredStart, maxBars - 1);

    let { start, end } = clampRange(desiredStart, desiredEnd, from, to);

    const currentDays = diffDays(start, end) + 1;
    if (currentDays < maxBars) {
        const missing = maxBars - currentDays;
        const canMoveStartBack = diffDays(from, start);
        const moveStartBack = Math.min(missing, Math.max(0, canMoveStartBack));
        start = addDays(start, -moveStartBack);

        const canMoveEndForward = diffDays(end, to);
        const remaining = missing - moveStartBack;
        const moveEndForward = Math.min(remaining, Math.max(0, canMoveEndForward));
        end = addDays(end, moveEndForward);
    }

    return { from: start, to: end };
};
/**
 * WEEKS mode:
 * - Weeks are rounded to Monday..Sunday.
 * - If the range is <= maxBars weeks: keep it (rounded to whole weeks).
 * - If range > maxBars weeks:
 *   - If the range contains the present week: choose a maxBars-week window placing the present
 *     week at index `maxBars - 2` — 9th of 10, 19th of 20 — **if the range reaches far enough
 *     into the future** (see {@link normalizePaymentsChartRange}).
 *   - If not: choose the maxBars-week window closest to the present week (head or tail).
 */
const getWeeksRange = (from: CalendarDate, to: CalendarDate, today: CalendarDate, maxBars = 10): { from: CalendarDate; to: CalendarDate } => {
    const fromWeek = startOf('week', 'UTC', from);
    const toWeek = startOf('week', 'UTC', to);
    const todayWeek = startOf('week', 'UTC', today);

    const totalWeeks = Math.floor(diffDays(fromWeek, toWeek) / 7) + 1;
    if (totalWeeks <= maxBars) return { from: fromWeek, to: endOf('week', 'UTC', to) };

    const includesPresentWeek = todayWeek >= fromWeek && todayWeek <= toWeek;
    if (!includesPresentWeek) {
        if (todayWeek < fromWeek) {
            const windowStart = fromWeek;
            const windowEndWeek = addWeeks(windowStart, maxBars - 1);
            return { from: windowStart, to: endOf('week', 'UTC', windowEndWeek) };
        }
        const windowEndWeek = toWeek;
        const windowStart = addWeeks(windowEndWeek, -(maxBars - 1));
        return { from: windowStart, to: endOf('week', 'UTC', windowEndWeek) };
    }

    const desiredStart = addWeeks(todayWeek, -(maxBars - 2));
    const desiredEndWeek = addWeeks(desiredStart, maxBars - 1);

    let { start, end } = clampRange(desiredStart, desiredEndWeek, fromWeek, toWeek);

    const currentWeeks = Math.floor(diffDays(start, end) / 7) + 1;
    if (currentWeeks < maxBars) {
        const missing = maxBars - currentWeeks;
        const canMoveStartBack = Math.floor(diffDays(fromWeek, start) / 7);
        const moveStartBack = Math.min(missing, Math.max(0, canMoveStartBack));
        start = addWeeks(start, -moveStartBack);

        const canMoveEndForward = Math.floor(diffDays(end, toWeek) / 7);
        const remaining = missing - moveStartBack;
        const moveEndForward = Math.min(remaining, Math.max(0, canMoveEndForward));
        end = addWeeks(end, moveEndForward);
    }

    return { from: start, to: endOf('week', 'UTC', end) };
};

/**
 * MONTHS mode:
 * - Months are rounded to whole calendar months.
 * - If range is <= maxBars months: keep it (rounded to month boundaries).
 * - If range > maxBars months:
 *   - If the range contains the present month: choose a maxBars-month window placing the present
 *     month at index `maxBars - 2` — 9th of 10, 19th of 20 — **if the range reaches far enough
 *     into the future** (see {@link normalizePaymentsChartRange}).
 *   - If not: choose the maxBars-month window closest to the present month (head or tail).
 */
const getMonthsRange = (from: CalendarDate, to: CalendarDate, today: CalendarDate, maxBars = 10): { from: CalendarDate; to: CalendarDate } => {
    const fromMonth = startOf('month', 'UTC', from);
    const toMonth = startOf('month', 'UTC', to);
    const todayMonth = startOf('month', 'UTC', today);

    const totalMonths = diffMonths(fromMonth, toMonth) + 1;
    if (totalMonths <= maxBars) return { from: fromMonth, to: endOf('month', 'UTC', to) };

    const includesPresentMonth = todayMonth >= fromMonth && todayMonth <= toMonth;
    if (!includesPresentMonth) {
        if (todayMonth < fromMonth) {
            const windowStart = fromMonth;
            const windowEndMonth = addMonths(windowStart, maxBars - 1);
            return { from: windowStart, to: endOf('month', 'UTC', windowEndMonth) };
        }
        const windowEndMonth = toMonth;
        const windowStart = addMonths(windowEndMonth, -(maxBars - 1));
        return { from: windowStart, to: endOf('month', 'UTC', windowEndMonth) };
    }

    const desiredStart = addMonths(todayMonth, -(maxBars - 2));
    const desiredEndMonth = addMonths(desiredStart, maxBars - 1);

    let { start, end } = clampRange(desiredStart, desiredEndMonth, fromMonth, toMonth);

    const currentMonths = diffMonths(start, end) + 1;
    if (currentMonths < maxBars) {
        const missing = maxBars - currentMonths;
        const canMoveStartBack = diffMonths(fromMonth, start);
        const moveStartBack = Math.min(missing, Math.max(0, canMoveStartBack));
        start = addMonths(start, -moveStartBack);

        const canMoveEndForward = diffMonths(end, toMonth);
        const remaining = missing - moveStartBack;
        const moveEndForward = Math.min(remaining, Math.max(0, canMoveEndForward));
        end = addMonths(end, moveEndForward);
    }

    return { from: start, to: endOf('month', 'UTC', end) };
};

/**
 * Public API. Three call sites, all passing `maxBars = 20`:
 * `dashboardService.getPaymentsChart`, `useReportsPayments`, `useReportsAccounts`.
 * (The `maxBars = 10` parameter defaults below describe the PHASE2 window — see the
 * PHASE1/PHASE2 toggle in `dashboardService.getPaymentsChart` — and no caller uses them.)
 *
 * Given an allowed [from..to] range (usually from UI preset selection), this returns the
 * effective range to request from the backend for charting:
 *
 * - **days**: window max `maxBars` days, try to place today at index `floor(maxBars / 2)`.
 * - **weeks**: window max `maxBars` weeks, round to Monday..Sunday, try to place the present
 *   week at index `maxBars - 2`.
 * - **months**: window max `maxBars` months, round to month boundaries, try to place the present
 *   month at index `maxBars - 2`.
 *
 * ## The placement only happens when the range reaches into the future
 *
 * "try to place" is doing real work in those three lines. The desired window is clamped to
 * [from..to] by {@link clampRange}, and any resulting shortfall is pushed back onto `start`
 * because `canMoveEndForward` is 0 once `end` has been clamped to `to`. So when `to <= today`,
 * the current period always ends up on the **last** bar, never at the index above.
 *
 * That is the normal case in this app, not an edge case: `useDateRangeWithTimezone` defaults to
 * `[today-29, today]`, so on Dashboard and both Reports tabs today is the last bar by design.
 * Only ranges extending past today — the "This week / month / quarter / year" picker presets, or
 * a manually picked future end date — give the placement rule the headroom it needs.
 *
 * Worked example at `maxBars = 20`, days mode:
 * - `[today-29, today]`     -> 20 bars, today at index 19 (last)
 * - `[today-29, today+9]`   -> 20 bars, today at index 10 (11th)
 *
 * @param tz  IANA timezone used to determine what "today" is. Defaults to browser timezone.
 */
export const normalizePaymentsChartRange = (
    from: string,
    to: string,
    viewMode: ChartViewMode,
    maxBars = 10,
    tz = browserTimezone(),
): { from: string; to: string } => {
    const today = todayIn(tz);

    if (viewMode === 'days') {
        const next = getDaysRange(from, to, today, maxBars);
        return { from: next.from, to: next.to };
    }

    if (viewMode === 'weeks') {
        const next = getWeeksRange(from, to, today, maxBars);
        return { from: next.from, to: next.to };
    }

    const next = getMonthsRange(from, to, today, maxBars);
    return { from: next.from, to: next.to };
};

export interface ChartBucket {
    from: CalendarDate;
    to: CalendarDate;
}

export const chartBucketRange = (
    from: CalendarDate,
    to: CalendarDate,
    viewMode: ChartViewMode,
    index: number,
): ChartBucket => {
    if (viewMode === 'days') {
        const day = addDays(from, index);
        return { from: day, to: day };
    }

    if (viewMode === 'weeks') {
        const start = addWeeks(from, index);
        const end = addDays(start, 6);
        return { from: start, to: end > to ? to : end };
    }

    const start = addMonths(from, index);
    const end = endOf('month', 'UTC', start);
    return { from: start, to: end > to ? to : end };
};

export type ChartBucketKind = 'cleared' | 'upcoming' | 'mixed';

export const chartBucketKind = (
    bucket: ChartBucket,
    tz = browserTimezone(),
): ChartBucketKind => {
    const today = todayIn(tz);
    if (bucket.to <= today) return 'cleared';
    if (bucket.from > today) return 'upcoming';
    return 'mixed';
};

export const rangeIntersectsCurrentChartPeriod = (
    from: string,
    to: string,
    viewMode: ChartViewMode,
    tz = browserTimezone(),
): boolean => {
    if (viewMode === 'days') {
        const today = todayIn(tz);
        return today >= from && today <= to;
    }

    const unit = viewMode === 'weeks' ? 'week' : 'month';
    const periodStart = startOf(unit, tz);
    const periodEnd = endOf(unit, tz);

    return from <= periodEnd && to >= periodStart;
};
